import { useState } from "react";
import api from "../lib/api";
import { ArrowRight, HandHeart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ExchangeHonor() {
  const [form, setForm] = useState({
    full_name: "", email: "", company: "", deal_summary: "",
    deal_value_m: 5, pledged_slice_pct: 2, counterparty_handle: "",
  });
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/exchange/honor-pledge", form);
      setResult(data);
      toast.success("Honor pledge recorded.");
    } catch { toast.error("Could not record pledge."); }
  };

  return (
    <>
      <section className="bg-[#064E3B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="honor-hero">
          <div className="federal-stamp" style={{ borderColor: "#FACC15", color: "#FACC15", background: "rgba(250,204,21,0.08)" }}>
            ★ HONOR PAY · DEAL-CONTINGENT
          </div>
          <h1 className="display-black text-5xl lg:text-6xl uppercase mt-6 leading-[0.95] tracking-tighter max-w-4xl">
            Meet on the Exchange.<br />Make money.<br /><span className="text-[#FACC15]">Honor a slice.</span>
          </h1>
          <p className="text-lg text-white/85 mt-6 max-w-2xl">
            The Exchange is free to join. If two members meet here and close a deal, they pledge a small slice — typically 1-3% — back to the platform that made it happen. We trust you. Honor it.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// How it works</div>
            <h2 className="display-black text-3xl uppercase text-[#064E3B] mt-3">Four moves.</h2>
            <ol className="mt-6 space-y-5">
              {[
                ["You join free.", "Verified members only. We don't charge to participate."],
                ["You post or browse.", "$1M floor. Premium deals only. No tire-kickers."],
                ["You connect + close.", "We facilitate the introduction. You close the deal."],
                ["You honor a slice.", "Pledge 1-3% of deal value back to the Exchange. On your word."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-5">
                  <div className="display-black text-2xl text-[#FACC15] bg-[#064E3B] w-12 h-12 flex items-center justify-center shrink-0">{i + 1}</div>
                  <div>
                    <div className="display-black text-lg uppercase text-[#064E3B]">{t}</div>
                    <div className="text-sm text-gray-700 mt-1">{d}</div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-10 border border-[#064E3B] bg-gray-50 p-6">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#064E3B]" />
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#064E3B]">Why honor?</div>
              </div>
              <p className="text-sm text-gray-800 mt-3">
                The Exchange runs on reputation. Members who honor pledges are publicly badged. Members who don't lose access. Simple.
              </p>
            </div>
          </div>

          <div>
            {result ? (
              <div className="border border-[#064E3B] bg-gray-50 p-10" data-testid="honor-success">
                <HandHeart className="w-10 h-10 text-[#D97706]" />
                <h2 className="display-black text-2xl uppercase text-[#064E3B] mt-5">Pledge Recorded.</h2>
                <p className="text-gray-800 mt-3">{result.message}</p>
                <div className="mt-6 grid grid-cols-2 gap-px bg-gray-300 border border-gray-300">
                  <div className="bg-white p-4">
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Deal Value</div>
                    <div className="display-black text-2xl text-[#064E3B] mt-1">${form.deal_value_m}M</div>
                  </div>
                  <div className="bg-white p-4">
                    <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Pledged</div>
                    <div className="display-black text-2xl text-[#D97706] mt-1">${result.pledged_amount_m}M</div>
                  </div>
                </div>
                <button onClick={() => setResult(null)} className="btn-ghost mt-6">Submit Another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-gray-300 p-7" data-testid="honor-form">
                <div className="md:col-span-2">
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Honor Pledge</div>
                  <h3 className="display-black text-xl uppercase text-[#064E3B] mt-2">Record your slice.</h3>
                </div>
                <input className="input-federal" required placeholder="Your name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="honor-name" />
                <input className="input-federal" required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="honor-email" />
                <input className="input-federal md:col-span-2" placeholder="Company / Firm" value={form.company} onChange={e => setForm({...form, company: e.target.value})} data-testid="honor-company" />
                <textarea className="input-federal md:col-span-2" rows={3} required placeholder="One-line deal summary" value={form.deal_summary} onChange={e => setForm({...form, deal_summary: e.target.value})} data-testid="honor-summary" />
                <input className="input-federal" placeholder="Counterparty handle (optional)" value={form.counterparty_handle} onChange={e => setForm({...form, counterparty_handle: e.target.value})} data-testid="honor-counterparty" />
                <input className="input-federal" type="number" min={1} step="0.5" required placeholder="Deal value $M" value={form.deal_value_m} onChange={e => setForm({...form, deal_value_m: parseFloat(e.target.value) || 0})} data-testid="honor-value" />
                <div className="md:col-span-2">
                  <label className="mono text-[10px] tracking-[0.22em] uppercase text-[#064E3B]">Pledged slice: {form.pledged_slice_pct}%</label>
                  <input type="range" min={0.5} max={10} step={0.5} value={form.pledged_slice_pct} onChange={e => setForm({...form, pledged_slice_pct: parseFloat(e.target.value)})} className="w-full mt-2" data-testid="honor-slice" />
                  <div className="mono text-[11px] tracking-wider text-[#D97706] uppercase mt-1">
                    Pledged amount: ${(form.deal_value_m * form.pledged_slice_pct / 100).toFixed(3)}M
                  </div>
                </div>
                <button type="submit" className="btn-signal md:col-span-2 justify-center" data-testid="honor-submit">Record Pledge <ArrowRight className="w-4 h-4" /></button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
